import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";
import { routes } from "../config/routes";
import { useCartStore } from "../store/use-cart-store";
import { useGetConfig } from "../store/use-config";
import { hasProductUpsellPackages } from "../utils/has-product-upsell-packages";

export type FlowStepId =
  | "select-package"
  | "select-date"
  | "choose-products"
  | "personal-details"
  | "confirm-order"
  | "done";

// Phone-verification and registration are sub-steps of the same "personal-details" step, so the
// indicator stays put across them. No route maps to the final "done" step — it stays unreached on
// purpose, so payment (confirm-order) shows as the second-to-last step. This signals there's still
// something to do after the summary, instead of "X/X" reading as "finished" and users abandoning
// before paying (REV-21).
const ROUTE_TO_STEP: Record<string, FlowStepId> = {
  [routes.catalog.raw()]: "select-package",
  [routes.selectTimeSlot.raw()]: "select-date",
  [routes.upsell.raw()]: "choose-products",
  [routes.phoneVerification.raw()]: "personal-details",
  [routes.registration.raw()]: "personal-details",
  [routes.summary.raw()]: "confirm-order",
};

export const useFlowSteps = () => {
  const { t } = useTranslation();
  const { getStandardProduct } = useCartStore();
  const config = useGetConfig();
  const hasUpsell = hasProductUpsellPackages(getStandardProduct());

  const catalogMatch = useRouteMatch({ path: routes.catalog.raw(), exact: true });
  const selectTimeSlotMatch = useRouteMatch({ path: routes.selectTimeSlot.raw(), exact: true });
  const upsellMatch = useRouteMatch({ path: routes.upsell.raw(), exact: true });
  const phoneVerificationMatch = useRouteMatch({ path: routes.phoneVerification.raw(), exact: true });
  const registrationMatch = useRouteMatch({ path: routes.registration.raw(), exact: true });
  const summaryMatch = useRouteMatch({ path: routes.summary.raw(), exact: true });

  // Steps are dynamic: the date step exists only with a calendar, the upsell step only when the
  // chosen product offers add-ons (so "X of Y" can grow by 1 after a product is picked on catalog).
  const steps: { id: FlowStepId; label: string }[] = [
    { id: "select-package", label: t("GROUP_ACTIONS_ORDERING.STEPS.SELECT_PACKAGE") },
  ];
  if (config.withCalendar) {
    steps.push({ id: "select-date", label: t("GROUP_ACTIONS_ORDERING.STEPS.SELECT_DATE") });
  }
  if (hasUpsell) {
    steps.push({ id: "choose-products", label: t("GROUP_ACTIONS_ORDERING.STEPS.CHOOSE_PRODUCTS") });
  }
  steps.push(
    { id: "personal-details", label: t("GROUP_ACTIONS_ORDERING.STEPS.ADD_PERSONAL_DETAILS") },
    { id: "confirm-order", label: t("GROUP_ACTIONS_ORDERING.STEPS.CONFIRM_ORDER") },
    { id: "done", label: t("GROUP_ACTIONS_ORDERING.STEPS.DONE") },
  );

  const currentRoute = (() => {
    if (summaryMatch) return routes.summary.raw();
    if (registrationMatch) return routes.registration.raw();
    if (phoneVerificationMatch) return routes.phoneVerification.raw();
    if (upsellMatch) return routes.upsell.raw();
    if (selectTimeSlotMatch) return routes.selectTimeSlot.raw();
    if (catalogMatch) return routes.catalog.raw();
    return undefined;
  })();

  const currentStepId = currentRoute ? ROUTE_TO_STEP[currentRoute] : undefined;
  const currentStepIndex = currentStepId ? steps.findIndex((step) => step.id === currentStepId) : -1;

  return {
    stepLabels: steps.map((step) => step.label),
    currentStepIndex,
  };
};
